import { useEffect, useMemo } from 'react';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { z } from 'zod';
import { Environment } from '@prisma/client';
import { isStablyEmail } from '~/common/stably-email/is-stably-email';
import { zVariableValidationType } from '~/common/variable/validation';
import { env } from '~/env.mjs';
import { api } from '~/trpc-extra/api';
import { useProjectId } from '~/hooks/context/useProjectId';
import { useViewModeValue } from '~/hooks/context/useViewMode';
import { Select, Tooltip, Button, ActionIcon, Switch } from '@mantine/core';
import { ENVIRONMENT_NAMES } from '~/common/environment/names';
import { VariableNameInput } from '~/components/common/VariableNameInput';
import { notifications } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import type { zVariableFormFields, zVariable } from '~/server/api/routers/variable';
import VariableValueInput from '~/components/common/VariableValueInput';

export type VariableForm = z.infer<typeof zVariableFormFields>;
export type Variable = z.infer<typeof zVariable>;

export default function VariableForm({
  variables,
  environmentScopedEnabled,
}: {
  variables: VariableForm[];
  environmentScopedEnabled: boolean;
}) {
  const projectId = useProjectId();
  const trpcUtils = api.useUtils();
  const viewMode = useViewModeValue(projectId);
  const { data: organizationEmailAddress } = api.stablyEmail.getOrganizationEmailAddress.useQuery({
    projectId: projectId!,
  });

  const { mutateAsync: createVariables, isPending: isCreatingVariables } = api.variable.batchCreate.useMutation({
    onError: (error) => {
      notifications.show({
        title: 'Error updating variables',
        message: error.message,
        color: 'red',
      });
    },
    onSuccess: async () => {
      await trpcUtils.variable.list.invalidate();
    },
  });

  const orgPrefix = organizationEmailAddress?.split('@').at(0)?.trim() || '';

  // Create validation schema with viewMode-aware email validation
  const zValidationSchema = useMemo(
    () =>
      z
        .object({
          environment: z.enum(Environment),
          newVariables: z.array(
            z
              .object({
                name: zVariableValidationType,
                value: z.string().optional(),
                sensitive: z.boolean(),
              })
              .superRefine((variable, ctx) => {
                if (!variable.sensitive && !variable.value) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['value'],
                    message: 'Variables must have a value',
                  });
                }
                // Only validate email format in classic mode (agent 2.0 doesn't use special email UI)
                if (viewMode === 'classic' && variable.value && isStablyEmail(variable.value)) {
                  const isValidFormat = new RegExp(
                    `^${orgPrefix}\\+[^\\s@]+@${env.NEXT_PUBLIC_STABLY_EMAIL_DOMAIN}$`,
                  ).test(variable.value);

                  if (!isValidFormat) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: ['value'],
                      message: 'Invalid email address format',
                    });
                  }
                }
              }),
          ),
        })
        .superRefine(({ environment, newVariables }, ctx) => {
          newVariables.forEach((variable, index) => {
            const duplicateVariables = variables.filter(
              (existingVariable) =>
                variable.name === existingVariable.name &&
                (environment === 'ALL' ||
                  existingVariable.environment === 'ALL' ||
                  environment === existingVariable.environment),
            );

            if (duplicateVariables.length > 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['newVariables', index, 'name'],
                message: `There is already a variable with this name${environmentScopedEnabled ? ' in this environment or for all environments' : ''}`,
              });
            }
          });
        }),
    [viewMode, organizationEmailAddress, variables, environmentScopedEnabled],
  );

  const form = useForm<{
    newVariables: VariableForm[];
    environment: Environment;
  }>({
    initialValues: {
      newVariables: [{ name: '', value: '', sensitive: false, environment: 'ALL' }],
      environment: 'ALL',
    },
    validate: zod4Resolver(zValidationSchema),
  });

  // If environment scoped is disabled, set environment to ALL (since all vars will be in ALL environment)
  useEffect(() => {
    if (!environmentScopedEnabled) {
      form.setFieldValue('environment', 'ALL');
    }
  }, [environmentScopedEnabled]);

  return (
    <form
      id="variables-form"
      onSubmit={form.onSubmit(async ({ newVariables, environment }) => {
        const variablesWithEnvironment = newVariables.map((variable) => ({
          ...variable,
          // New variables are now stored as strings, so we need to convert the value to a string.
          value: String(variable.value ?? ''),
          environment,
        }));

        await createVariables({ projectId: projectId!, variables: variablesWithEnvironment });
        form.reset();
      })}
    >
      {!!environmentScopedEnabled && (
        <Select
          className="mb-4 w-48"
          label="Environment Scope"
          placeholder="Select environment"
          data={Object.entries(ENVIRONMENT_NAMES)
            .filter(([environment]) => environment !== 'NONE')
            .map(([environment, environmentName]) => ({
              value: environment,
              label: environmentName,
            }))}
          allowDeselect={false}
          {...form.getInputProps('environment')}
        />
      )}

      <div className="flex w-full flex-col gap-4">
        {form.values.newVariables.map(({ id, name, sensitive, environment }, index) => {
          return (
            <div
              key={`${id}-${environment}`}
              className="flex w-full items-start gap-4 py-1"
            >
              <VariableNameInput
                className="w-48"
                value={name}
                placeholder="Name"
                label=""
                {...form.getInputProps(`newVariables.${index}.name`)}
              />
              <VariableValueInput
                className="w-64"
                orgPrefix={orgPrefix}
                sensitive={sensitive}
                {...form.getInputProps(`newVariables.${index}.value`)}
              />

              <div className="flex items-start gap-2 pt-1">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  title="Remove Variable"
                  onClick={() => form.removeListItem('newVariables', index)}
                >
                  <IconTrash size={16} />
                </ActionIcon>
                <Tooltip
                  withArrow
                  w={220}
                  multiline
                  color="gray"
                  label="If enabled, you and your team will not be able to read the value of this variable, or change it back to non-sensitive."
                  disabled={sensitive}
                >
                  <div>
                    <Switch
                      className="pt-1"
                      label="Sensitive"
                      checked={sensitive}
                      onChange={(event) => {
                        form.setFieldValue(`newVariables.${index}.sensitive`, event.currentTarget.checked);
                      }}
                    />
                  </div>
                </Tooltip>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-start gap-4 py-4">
        <Button
          variant="transparent"
          className="pl-0"
          leftSection={<IconPlus size={16} />}
          onClick={() => {
            form.insertListItem('newVariables', {
              name: '',
              value: '',
              sensitive: false,
              environment: 'ALL' as const,
            });
          }}
        >
          Add Variable
        </Button>
        <Button
          type="submit"
          loading={isCreatingVariables}
          disabled={!form.isDirty()}
        >
          Save
        </Button>
      </div>
    </form>
  );
}
